//  Copyright 2019 Siemens Product Lifecycle Management Software Inc.
//
//  This is a Header Class for Atlas worker communication
//  the header type are
//  "A" - Api data
//  "S" - Graphics Structure data
//  "G" - Graphics Geometry  data
//  "N" - Tracking object data
//  "P" - Progress Bar data  
//  "U" - User Defined data  
//
//  usage
//  let header = new Header()
// 
// to encode a header for data of type ArrayBuffer
// to decode a header user decodeHeader

const bufferSize = 16;
const correlationIdSize = 16;
const transactionIdSize = 16;

const uuidFormat = /^[0-9a-f]{8}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{4}\b-[0-9a-f]{12}$/i;

function Header() {

    Header.prototype.HeaderOptions = { COMPRESS: 1, USE_CORRELATION_ID: 2, USE_TRANSACTION_ID: 4, NON_KEY_FRAME: 8 };
    Header.prototype.MessageCodes = {
        API: 'A',
        GRAPHICS_STRUCTURE: 'S',
        GRAPHICS_GEOMETRY: 'G',
        TRACKING_OBJECT: 'N',
        PROGRESS_BAR: 'P',
        USER_DEFINED: 'U',
        END: 'F'
    };

    // <dass> These declarations are kept so that any client code using these constants does not break.
    Header.prototype.COMPRESS = this.HeaderOptions.COMPRESS;
    Header.prototype.CORRELATION = this.HeaderOptions.USE_CORRELATION_ID;
    Header.prototype.TRANSACTION = this.HeaderOptions.USE_TRANSACTION_ID;
    Header.prototype.NON_KEY_FRAME = this.HeaderOptions.NON_KEY_FRAME;

    Header.prototype.getBufferSize = function (data, optionMask) {
        if (data === undefined && optionMask === undefined) {
            throw new Error('Can not calculate buffersize- both data and option mask are undefined!!');
        }
        let size = bufferSize;
        if (optionMask === undefined) {
            if (data instanceof ArrayBuffer) {
                if (data.byteLength >= bufferSize) {
                    const view = new DataView(data);
                    const optionMask = view.getUint8(12);
                    if (optionMask & this.HeaderOptions.USE_CORRELATION_ID) {
                        size += correlationIdSize;
                    }
                    if (optionMask & this.HeaderOptions.USE_TRANSACTION_ID) {
                        size += transactionIdSize;
                    }
                }
            }
            else {
                if (data.length >= bufferSize) {
                    const optionMask = data[12];
                    if (optionMask & this.HeaderOptions.USE_CORRELATION_ID) {
                        size += correlationIdSize;
                    }
                    if (optionMask & this.HeaderOptions.USE_TRANSACTION_ID) {
                        size += transactionIdSize;
                    }
                }
            }
        } else {
            if (optionMask & this.HeaderOptions.USE_CORRELATION_ID) {
                size += correlationIdSize;
            }
            if (optionMask & this.HeaderOptions.USE_TRANSACTION_ID) {
                size += transactionIdSize;
            }
        }
        return size;
    }

    Header.prototype.validateUUID = function(id) {
        return uuidFormat.test(id);
    }

    Header.prototype.generateHeader = function (data, code, id, optionMask, sequence, correlation, transactionId) {
        let length = data.length;
        const size = this.getBufferSize(data, optionMask);
        let header = new ArrayBuffer(size + length);
        let view = new DataView(header);
        view.setUint8(0, code, false); // F == End
        view.setUint8(1, 0, false); // end Code
        if (sequence === undefined)
            view.setUint16(2, 0, false); // SequenceNumer
        else
            view.setUint16(2, sequence, false);
        view.setUint32(4, id, false); // 
        view.setUint32(8, data.length, false); // buffer size
        view.setUint8(12, optionMask, false); // optionMask 
        let offset = bufferSize;
        if (optionMask & this.HeaderOptions.USE_CORRELATION_ID) {
            let uuid;
            if (correlation !== undefined) {
                if (typeof correlation == 'string' && correlation.length == 32) {
                    uuid = correlation;
                }
            }
            if (uuid === undefined) {
                uuid = this.generateCorrelationId();
            }
            offset = this._addUUID(view, offset, uuid);
        }
        if (optionMask & this.HeaderOptions.USE_TRANSACTION_ID) {
            if (transactionId === undefined) {
                throw new Error(`Missing transaction id.`);
            }
            if (!this.validateUUID(transactionId)) {
                throw new Error(`Transaction ID is not a valid UUID.`);
            }
            transactionId = transactionId.replace(/-/g, '');
            offset = this._addUUID(view, offset, transactionId)
        }

        if (code == 65 && ((optionMask & this.HeaderOptions.COMPRESS) === 0)) {
            for (let i = 0; i < length; i++) {
                view.setUint8(offset + i, data.charCodeAt(i), false);
            }
        }
        else {
            for (let i = 0; i < length; i++) {
                view.setUint8(offset + i, data[i], false);
            }
        }
        return header;
    }

    Header.prototype.decodeHeader = function (data) {
        const self = this;
        let result = {};
        result.isBinary = function () {
            return this.code !== undefined &&
                this.optionMask !== undefined &&
                this.validCode !== undefined &&
                result.endCode == 0;
        }
        const buffSize = this.getBufferSize(data);
        if (data instanceof ArrayBuffer) {
            if (data.byteLength >= buffSize) {
                let view = new DataView(data);

                let code = String.fromCharCode(view.getUint8(0));
                let endCode = view.getUint8(1);
                if ((code === this.MessageCodes.API || code === this.MessageCodes.GRAPHICS_GEOMETRY || code === this.MessageCodes.GRAPHICS_STRUCTURE ||
                    code === this.MessageCodes.END || code === this.MessageCodes.TRACKING_OBJECT || code === this.MessageCodes.PROGRESS_BAR || code === this.MessageCodes.USER_DEFINED)
                    && endCode == 0) {
                    result.code = code;
                    result.validCode = true;
                    result.endCode = endCode;
                    result.sequence = (view.getUint8(2) << 8) + view.getUint8(3);
                    result.id = (view.getUint8(4) << 24) + (view.getUint8(5) << 16) + (view.getUint8(6) << 8) + view.getUint8(7);
                    result.size = (view.getUint8(8) << 24) + (view.getUint8(9) << 16) + (view.getUint8(10) << 8) + view.getUint8(11);
                    result.optionMask = view.getUint8(12);
                    result.headSize = buffSize;
                    let offset = 0;
                    if (result.optionMask & this.HeaderOptions.USE_CORRELATION_ID) {
                        result.correlationId = this.getUUID(data);
                        offset = correlationIdSize;
                    }
                    if (result.optionMask & this.HeaderOptions.USE_TRANSACTION_ID) {
                        result.transactionId = this.getUUID(data, bufferSize + offset, true);
                    }
                }
            }
        }
        else {
            if (data.length >= buffSize) {
                let code = String.fromCharCode(data[0]);
                let endCode = data[1];
                if ((code === this.MessageCodes.API || code === this.MessageCodes.GRAPHICS_GEOMETRY || code === this.MessageCodes.GRAPHICS_STRUCTURE ||
                    code === this.MessageCodes.END || code === this.MessageCodes.TRACKING_OBJECT || code === this.MessageCodes.PROGRESS_BAR || code === this.MessageCodes.USER_DEFINED)
                    && endCode == 0) {
                    result.code = code;
                    result.validCode = true;
                    result.endCode = endCode;
                    result.sequence = (data[2] << 8) + data[3];
                    result.id = (data[4] << 24) + (data[5] << 16) + (data[6] << 8) + data[7];
                    result.size = (data[8] << 24) + (data[9] << 16) + (data[10] << 8) + data[11];
                    result.optionMask = data[12];
                    result.headSize = buffSize;
                    let offset = 0;
                    if (result.optionMask & this.HeaderOptions.USE_CORRELATION_ID) {
                        result.correlationId = this.getUUID(data);
                        offset = correlationIdSize;
                    }
                    if (result.optionMask & this.HeaderOptions.USE_TRANSACTION_ID) {
                        result.transactionId = this.getUUID(data, bufferSize + offset, true);
                    }
                }
            }
        }

        if (result.code) {
            result.isEnd = function () { return this.code === self.MessageCodes.END; }
            result.isAPI = function () { return this.code === self.MessageCodes.API; }
            result.isGraphicsStructure = function () { return this.code === self.MessageCodes.GRAPHICS_STRUCTURE; }
            result.isGraphicsGeometry = function () { return this.code === self.MessageCodes.GRAPHICS_GEOMETRY; }
            result.isNavigator = function () { return this.code === self.MessageCodes.TRACKING_OBJECT; }
            result.isProgressBar = function () { return this.code === self.MessageCodes.PROGRESS_BAR; }
            result.isUserDefined = function () { return this.code === self.MessageCodes.USER_DEFINED; }

            result.getSize = function () { return this.size; }
            result.getId = function () { return this.id; }
            result.getOptionMask = function () { return this.optionMask; }
            result.isCompress = function () { return this.optionMask & self.HeaderOptions.COMPRESS; }
            result.hasCorrelation = function () { return this.optionMask & self.HeaderOptions.USE_CORRELATION_ID; }
            result.hasTransaction = function () { return this.optionMask & self.HeaderOptions.USE_TRANSACTION_ID; }
            result.isKeyFrame = function () { return !(this.optionMask & self.HeaderOptions.NON_KEY_FRAME); }
            result.getSequence = function () { return this.sequence; }
            result.getHeaderSize = function () {
                let size = bufferSize;
                if (this.hasCorrelation()) {
                    size += correlationIdSize;
                }
                if (this.hasTransaction()) {
                    size += transactionIdSize;
                }
                return size;
            }
            result.getCorrelationId = function () {
                if (this.hasCorrelation()) {
                    return this.correlationId;
                }
                return null;
            }
            result.getTransactionId = function () {
                if (this.hasTransaction()) {
                    return this.transactionId;
                }
                return null;
            }
            result.debugType = function () {
                if (this.code === self.MessageCodes.END) {
                    return 'End';
                }
                if (this.code === self.MessageCodes.API) {
                    return 'API';
                }
                if (this.code === self.MessageCodes.GRAPHICS_STRUCTURE) {
                    return 'Graphics Structure';
                }
                if (this.code === self.MessageCodes.GRAPHICS_GEOMETRY) {
                    return 'Graphics Geometry';
                }
                if (this.code === self.MessageCodes.TRACKING_OBJECT) {
                    return 'Tracking';
                }
                if (this.code === self.MessageCodes.PROGRESS_BAR) {
                    return 'Progress Bar';
                }
                if (this.code === self.MessageCodes.USER_DEFINED) {
                    return 'User Defined';
                }
                return '';
            }
            result.debugString = function () {
                let data = {};
                data.type = this.debugType();
                data.id = this.id;
                data.sequence = this.sequence;
                data.size = this.size;
                data.compress = this.optionMask & self.HeaderOptions.COMPRESS;
                if (this.hasCorrelation()) {
                    data.correlation = this.correlationId;
                }
                if (this.hasTransaction()) {
                    data.transaction = this.transactionId;
                }
                data.keyFrame = !(this.optionMask & self.HeaderOptions.NON_KEY_FRAME);
                return JSON.stringify(data);
            }
            return result;
        }
    }
    Header.prototype.generateCorrelationId = function () {
        if (typeof crypto === 'undefined') {
            let d = new Date().getTime();
            return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                let r = (d + Math.random() * 16) % 16 | 0;
                d = Math.floor(d / 16);
                return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
            });
        }
        if (crypto.randomBytes !== undefined) {
            let buffer = crypto.randomBytes(16);
            return buffer.toString('hex');
        }
        else if (crypto.getRandomValues !== undefined) {
            let a = (new Date().getTime()).toString();
            while (a.length < 32) {
                let r = crypto.getRandomValues(new Uint8Array(1))[0];
                if (r < 127) {
                    a = a + r.toString();
                }
                else {
                    a = r.toString() + a;
                }
            }
            a = a.slice(a.length - 32);
            return a.replace(/[0123456789]/g, c => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
        }
    }
    Header.prototype._addUUID = function (headerView, start, uuid) {
        for (let i = 0; i < correlationIdSize; i++) {
            let left = Number(`0x${uuid[i * 2].substr(0)}`) << 4;
            let right = Number(`0x${uuid[i * 2 + 1].substr(0)}`);

            headerView.setUint8(start + i, left + right);
        }
        return start + correlationIdSize;
    }
    Header.prototype.getUUID = function (data, offset = bufferSize, addDashes  = false) {
        let uuid = undefined;
        if (data instanceof ArrayBuffer) {
            if (data.byteLength >= this.getBufferSize(data)) {
                let view = new DataView(data);
                uuid = '';
                for (let i = 0; i < correlationIdSize; i++) {
                    let left = view.getUint8(offset + i) >> 4;

                    let right = view.getUint8(offset + i) - (left << 4);

                    uuid += left.toString(16) + right.toString(16);
                }

            }
        }
        else if (data.length >= this.getBufferSize(data)) {
            uuid = '';
            for (let i = 0; i < correlationIdSize; i++) {
                let left = data[offset + i] >> 4;

                let right = data[offset + i] - (left << 4);

                uuid += left.toString(16) + right.toString(16);
            }
        }
        if (uuid !== undefined && addDashes) {
            return uuid.slice(0, 8) + '-' + uuid.slice(8, 12) + '-' + uuid.slice(12, 16) + '-' + uuid.slice(16, 20) + '-' + uuid.slice(20);
        }
        return uuid;
    }

    // wrapper method
    Header.prototype.getCorrelationId  = function (data) {
        return this.getUUID(data);
    }
}

module.exports = { Header };
